import { create } from "zustand";
import { Module } from "../accessibility-management/api/types";
import { userData } from "./api/types";

export interface IAccessibilityModalState {
  modules: Module[];
  setModule: (val: Module[]) => void;

  isDefault: boolean;
  setIsDefault: (val: boolean) => void;

  isGlobal: boolean;
  setIsGlobal: (val: boolean) => void;

  toggleModule: (moduleId: string) => void;
  togglePrivilege: (moduleId: string, privilegeId: string) => void;
  enableAllPrivilegesInModule: (moduleId: string) => void;
  enableAll: () => void;

  isManageAccessModalOpen: boolean;
  setManageAccessModal: (val: boolean) => void;

  userData: userData | undefined;
  setUserData: (val: userData | undefined) => void;

}

const useAccessibilityModalStore = create<IAccessibilityModalState>(
  (set, get) => ({
    modules: [],
    setModule: (modules) => set({ modules }),

    userData: undefined,
    setUserData: (userData) => set({ userData }),

    isManageAccessModalOpen: false,
    setManageAccessModal: (isManageAccessModalOpen: boolean) =>
    set({ isManageAccessModalOpen }),

    toggleModule: (moduleId: string) => {
      set((state) => ({
        modules: state.modules.map((module) => {
          if (module.id === moduleId) {
            // Toggle the module's enabled state
            const newModuleEnabledState = !module.enable;

            // If the module is being disabled, disable all its privileges
            const updatedPrivileges = newModuleEnabledState
              ? module.privileges
              : module.privileges.map((privilege) => ({
                  ...privilege,
                  enable: false,
                }));

            return {
              ...module,
              enable: newModuleEnabledState,
              privileges: updatedPrivileges,
            };
          }
          return module;
        }),
      }));
    },

    togglePrivilege: (moduleId: string, privilegeId: string) => {
      set((state) => ({
        modules: state.modules.map((module) =>
          module.id === moduleId
            ? {
                ...module,
                privileges: module.privileges.map((privilege) =>
                  privilege.id === privilegeId
                    ? { ...privilege, enable: !privilege.enable }
                    : privilege
                ),
              }
            : module
        ),
      }));
    },

    enableAllPrivilegesInModule: (moduleId: string) => {
      set((state) => {
        // Find the module
        const moduleToUpdate = state.modules.find(
          (module) => module.id === moduleId
        );

        if (!moduleToUpdate) {
          return state;
        }

        // Check if all privileges are enabled
        const areAllEnabled = moduleToUpdate.privileges.every(
          (privilege) => privilege.enable
        );

        // Determine the new 'enable' state (true to enable, false to disable)
        const newEnableState = !areAllEnabled;

        return {
          modules: state.modules.map((module) =>
            module.id === moduleId
              ? {
                  ...module,
                  privileges: module.privileges.map((privilege) => ({
                    ...privilege,
                    enable: newEnableState,
                  })),
                }
              : module
          ),
        };
      });
    },

    enableAll: () => {
      set((state) => {
        // Check if ALL modules and privileges are currently enabled
        const areAllEnabled = state.modules.every(
          (module) =>
            module.enable &&
            module.privileges.every((privilege) => privilege.enable)
        );

        // The new state will be the opposite of the current state
        const newEnableState = !areAllEnabled;

        return {
          modules: state.modules.map((module) => ({
            ...module,
            enable: module.name === "User Profile" ? true : newEnableState,
            privileges: module.privileges.map((privilege) => ({
              ...privilege,
              enable: newEnableState,
            })),
          })),
        };
      });
    },

    isDefault: false,
    setIsDefault: (isDefault: boolean) => set({ isDefault }),

    isGlobal: false,
    setIsGlobal: (isGlobal: boolean) => set({ isGlobal }),
  })
);

export default useAccessibilityModalStore;
